import { Paper, Typography } from '@mui/material';
import React, { useState } from 'react';

import Button from '@mui/material/Button';
import { CollectionVm } from '../../../content/models/ResourceModel/Collection';
import CourseVm from '../../../content/models/CourseVm';
import { CustomLoadingProgress } from '../CustomLoadingProgress';
import DisplaySettings from '../../../dashboard/models/DisplaySettings';
import Grid from '@mui/material/Unstable_Grid2';
import Lesson from '../../../content/models/Lesson';
import ModuleSearchModel from '../../../content/models/SearchModel/ModuleSearchModel';
import { ResourceCollectionModel } from '../../../content/models/SearchModel/ResourceCollectionModel';
import { ResourceLinkModel } from '../../../content/models/ResourceModel/ResourceLink';
import { ResourceLinkSearchModel } from '../../../content/models/SearchModel/ResourceLinkSearchModel';
import SearchList from './SearchList';
import SearchService from './SearchService';
import SearchTextField from '../../../dashboard/components/SearchTextField';
import styles from '../../styles/Keyword.module.css';
import { useNavigate } from 'react-router-dom';
import { useSearchContent } from '../../../dashboard/hooks/useSearchContent';
import { useTheme } from '@mui/material';

interface IResults {
  /**  `coursekey`/`lessonkey`/`collectionkey` */
  id: number;
  /** Title of `course`/`lesson`/`collection`/`resource` */
  title: string;
  description: string;
  tags?: string;
  parentTitle?: string;
  displaySettings?: DisplaySettings;
  /** Resource id */
  position?: number;
  modulekey?: number;
}

interface SearchResults {
  course: IResults[];
  lesson: IResults[];
  collection: IResults[];
  resource: IResults[];
}

export const SEARCH_VALUES = [
  'course',
  'module',
  'collection',
  'resource'
] as const;
export type SearchBy = (typeof SEARCH_VALUES)[number];

interface IKeywordSearchProps {
  isAuthorSearch?: boolean;
  searchString?: string;
}

const KeywordLocate = (props: IKeywordSearchProps) => {
  const { isAuthorSearch, searchString } = props;
  const [searchText, setSearchText] = useState<string>('');
  const [results, setResults] = useState<SearchResults>();
  const [isResultsEmpty, setIsResultsEmpty] = useState(false);
  const navigate = useNavigate();
  const { getSearch, searching, searhResults } = useSearchContent();
  const querySearchParam = SearchService.searchParam;
  const queryhighlightParam = SearchService.highlightParam;
  const muiTheme = useTheme();
  const primaryColor = muiTheme.palette.primary.main;

  const handleCardClick = (
    k: string,
    id: number,
    lessonkey?: number,
    position?: number
  ) => {
    switch (k as keyof SearchResults) {
      case 'collection':
        navigate(
          `/${
            isAuthorSearch ? 'content/' : ''
          }resources/${id}?${querySearchParam}=${searchText}`
        );
        break;
      case 'resource':
        navigate(
          `/${
            isAuthorSearch ? 'content/' : ''
          }resources/${id}?${querySearchParam}=${searchText}&${queryhighlightParam}=${position}`
        );
        break;
      case 'course':
        navigate(
          `/${isAuthorSearch ? 'content' : 'dashboard'}/course${
            isAuthorSearch ? 's' : ''
          }/${id}?${querySearchParam}=${searchText}`
        );
        break;
      case 'lesson':
        const modulekey = results.lesson.find(
          (m) => m.modulekey === lessonkey
        )?.modulekey;
        navigate(
          `/${isAuthorSearch ? 'content' : 'dashboard'}/course${
            isAuthorSearch ? 's' : ''
          }/${id}?${querySearchParam}=${searchText}&${queryhighlightParam}=${modulekey}`
        );
        break;
      default:
        break;
    }
  };

  const handleReturn = () => {
    navigate(`${isAuthorSearch ? '/content' : '/dashboard'}/locate`);
  };

  const handleSearchValue = (value: string) => {
    setSearchText(value);
    if (!value) {
      setResults(undefined);
    }
  };

  const handleSearch = async () => {
    let aggregatedResults: SearchResults = {
      course: [],
      collection: [],
      lesson: [],
      resource: []
    };

    const searchResultsArray = await Promise.all(
      SEARCH_VALUES.map(async (s) => {
        const res = await getSearch({
          searchBy: s,
          searchText: searchText,
          isAuthorSearch
        });

        const resultItem: Partial<SearchResults> = {};
        switch (s) {
          case 'course':
            resultItem.course =
              res?.map((r) => {
                const course = r as CourseVm;
                return {
                  id: course.coursekey,
                  title: course.title,
                  description: course.description,
                  tags: course.tags,
                  displaySettings: course.assets.icon
                };
              }) || [];
            break;
          case 'module':
            resultItem.lesson =
              res?.map((r) => {
                const lesson = r as ModuleSearchModel;
                return {
                  id: lesson.coursekey,
                  title: lesson.title,
                  description: lesson.description,
                  tags: lesson.tags,
                  parentTitle: lesson.coursetitle,
                  displaySettings: lesson.assets.background,
                  modulekey: lesson.modulekey
                };
              }) || [];
            break;
          case 'collection':
            resultItem.collection =
              res?.map((r) => {
                const collection = r as ResourceCollectionModel;
                return {
                  id: collection.collectionkey,
                  title: collection.title,
                  description: collection.description,
                  tags: collection.tags,
                  displaySettings: collection.assets.icon
                };
              }) || [];
            break;
          case 'resource':
            resultItem.resource =
              res?.map((r) => {
                const resource = r as ResourceLinkSearchModel;
                return {
                  id: resource.collectionkey,
                  title: resource.title,
                  description: resource.description,
                  tags: resource.tags,
                  parentTitle: resource.collectiontitle,
                  position: resource.position
                };
              }) || [];
            break;
        }

        return resultItem;
      })
    );

    // Merge all search results into `aggregatedResults`
    searchResultsArray.forEach((result) => {
      Object.entries(result).forEach(([key, value]) => {
        aggregatedResults[key as keyof SearchResults].push(...value);
      });
    });
    // Check if all results arrays are empty
    const isEmpty = Object.values(aggregatedResults).every(
      (arr) => arr.length === 0
    );
    setIsResultsEmpty(isEmpty);

    setResults(aggregatedResults);
  };

  return (
    <Grid
      m={1}
      container
      direction={'column'}
      className={isAuthorSearch ? styles.keywordAuthoring : ''}
    >
      <Grid>
        <Typography variant="subtitle1">Keywords</Typography>
      </Grid>
      <Paper
        elevation={2}
        style={{
          border: `2px solid ${primaryColor}`,
          boxShadow: `1px 0px 5px 1px rgba(0, 0, 0, 0.2)`
        }}
      >
        <Grid
          container
          direction={'column'}
          alignItems={'center'}
          sx={{ padding: 2 }}
        >
          <Grid width="100%">
            <SearchTextField
              noLabel
              width="100%"
              value={searchString}
              onReturn={handleReturn}
              onSearch={handleSearchValue}
            />
          </Grid>
          <Grid alignSelf={'end'} marginBottom={'-10px'}>
            {searchString ? (
              <Button
                variant="text"
                color="primary"
                size="small"
                onClick={handleReturn}
              >
                Return to locate page
              </Button>
            ) : (
              <Button
                variant="text"
                color="primary"
                disabled={!searchText}
                onClick={handleSearch}
              >
                Search
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {searching && <CustomLoadingProgress style={{ padding: 2 }} />}
      {!searching && isResultsEmpty && (
        <Typography variant="subtitle1" sx={{ paddingTop: '8px' }}>
          Sorry, no results found for the search criteria.
        </Typography>
      )}
      {!searching &&
        results &&
        Object.keys(results)?.map((k) => {
          const res = results[k as keyof SearchResults];
          return (
            res?.length > 0 && (
              <Grid key={k} container pt={'8px'} direction={'column'}>
                <Grid pb={'4px'}>
                  <Typography variant="body2">
                    {['course', 'collection'].includes(k)
                      ? k?.toUpperCase()
                      : ''}
                  </Typography>
                </Grid>

                <Grid container spacing={1} direction={'column'}>
                  {res?.map((r) => (
                    <Grid key={`${r.id}-${r.title}`}>
                      <Grid pb={'4px'}>
                        <Typography variant="body2">
                          {k === 'lesson'
                            ? `LESSON | COURSE: ${r.parentTitle}`
                            : k === 'resource'
                            ? `RESOURCE | COLLECTION: ${r.parentTitle}`
                            : ''}
                        </Typography>
                      </Grid>
                      <Paper
                        elevation={2}
                        sx={{ padding: '4px', cursor: 'pointer' }}
                        onClick={() =>
                          handleCardClick(k, r.id, r.modulekey, r.position)
                        }
                      >
                        <SearchList
                          title={r.title}
                          description={r.description}
                          tags={r.tags}
                          searchText={searchText}
                          displaySettings={r.displaySettings}
                        />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )
          );
        })}
    </Grid>
  );
};

export default KeywordLocate;
